import { JogId, Jog, AgencyId, Agency } from ".";
import Agencies from "./agencies";

const isUserJog = true;
const notIsUserJog = !isUserJog;
const isGroupOnly = true;
const isFilter = true;
const notIsFilter = !isFilter;

const makeJog = (id: JogId, name: string, shortName: string, isFilter = true, isUserJog = true, isGroupOnly = false): Jog => {
  return {
    id,
    name,
    shortName,
    agencies: getJogAgencies(id, Agencies),
    isFilter,
    isUserJog,
    isGroupOnly,
  }
}

const agencyJogsMap = new Map<AgencyId, Set<JogId>>();

const getJogAgencies = (jogId: JogId, agencies: Agency[]): Agency[] => {
  const jogAgencies: Agency[] = [];
  agencies.forEach((agency) => {
    if (agency.jogId === jogId) {
      // Add to JOG's agencies
      jogAgencies.push(agency);
      
      // Add to agencyJogsMap
      let set = agencyJogsMap.get(agency.id);
      if(set) {
        set.add(agency.jogId);
      } else {
        agencyJogsMap.set(agency.id, new Set([agency.jogId]));
      }      
    }
  })

  return jogAgencies;
}

const Jogs: Jog[] = [
  makeJog(JogId.Other, "Other/Unassigned", "Other", notIsFilter, notIsUserJog),
  
  // "Participating"
  makeJog(JogId.ClarkCounty, "Clark County JOG", "Clark Co."),
  makeJog(JogId.JBLM, "Joint Base Lewis-McChord JOG", "JBLM"),
  makeJog(JogId.KingCountyNortheast, "Northeast King County JOG", "NEJOG"),
  makeJog(JogId.KingCountySouth, "South King County JOG", "South JOG"),
  makeJog(JogId.KitsapPeninsula, "Kitsap Peninsula JOG", "Kitsap Pen."),
  makeJog(JogId.LowerColumbiaRiver, "Lower Columbia River JOG", "Lower Col. River"),
  makeJog(JogId.NorthwestWashington, "Northwest WA JOG", "NW WA"),
  makeJog(JogId.SeattleArea, "Seattle Area JOG", "SAJOG"),
  makeJog(JogId.SnohomishCounty, "Snohomish County JOG", "Snohomish Co."),
  makeJog(JogId.Tacoma, "Tacoma JOG", "Tacoma"),

  // "Group" (not an actual JOG)
  makeJog(JogId.EasternWashington, "Eastern WA Group", "Eastern WA", isFilter, isUserJog, isGroupOnly), // Temp group for areas covered by WSP districts 3, 4, 6. Not a JOG.
  makeJog(JogId.Wsp, "Washington State Patrol Districts", "WSP Districts", isFilter, notIsUserJog, isGroupOnly),
]

// eslint-disable-next-line
const futureJogs: Jog[] = [
  // Not finalized
  // makeJog(JogId.ThurstonCounty, "Thurston County JOG", "Thurston Co.", notIsFilter, notIsUserJog),
  makeJog(JogId.TriCities, "Tri-Cities JOG", "Tri-Cities", notIsFilter, notIsUserJog),
  makeJog(JogId.LewisCounty, "Lewis County JOG", "Lewis Co.", notIsFilter, notIsUserJog),
  makeJog(JogId.Spokane, "Spokane JOG", "Spokane", notIsFilter, notIsUserJog),
]

const JogMap = ((): Map<string, Jog> => {
  let jogMap = new Map<string, Jog>();
  Jogs.forEach((jog) => {
    jogMap.set(jog.id, jog)
  })
  
  return jogMap;
})();

export const GetJog = (jogId: JogId | string): Jog | undefined => {
  return JogMap.get(jogId);
}

export const JogsForUsers = Jogs
  .filter(jog => jog.isUserJog)
  .sort((a, b) => a.shortName < b.shortName ? -1 : 1);

export const JogsForDispatchFiltering = Jogs
  .filter(jog => jog.isFilter)
  .sort((a, b) => a.shortName < b.shortName ? -1 : 1);

export const JogsForEventsAndUsers = Jogs
  .filter(j => j.isFilter && !j.isGroupOnly)
  .sort((j1, j2) => j1.name > j2.name ? 1 : -1);

export const GetJogNames = (jogIds: string | string[] | undefined, fullName = false): string[] => {
  if (!jogIds) return [];

  // if string, change to array
  if (typeof jogIds === "string") {
    jogIds = jogIds.split(",");
  }
  
  const other = JogMap.get(JogId.Other);
  const jogNameOther = (fullName ? other?.name : other?.shortName) || "";
  let jogNames = jogIds.map((id) => {
    const jog = JogMap.get(id);
    return (fullName ?  jog?.name : jog?.shortName) || "";
  });
  jogNames = jogNames?.filter((x) => !!x && x !== jogNameOther).sort();

  return jogNames;
}

// Returns a Set of all jogIds for JOGs the agency is associated with.
export const GetAgencyJogIds = (id: AgencyId): Set<JogId> | undefined => {
  return agencyJogsMap.get(id);
}

// Returns Jog objects for all JOGs an Agency is associated with
export const GetAgencyJogs = (id: AgencyId): Jog[] => {
  const agencyJogIds = GetAgencyJogIds(id) || new Set<JogId>();

  // Order matters
  const jogs = Jogs
    .filter(jog => jog.isUserJog && agencyJogIds.has(jog.id))     // filter creates a new array,
    .sort((j1, j2) => { return (j1.name > j2.name) ? 1 : -1; });  // then it's sorted in place.

    return jogs;
}